import { useEffect, useState, useRef, useCallback } from 'react';
import { X, Maximize2, Volume2, VolumeX, Play, Pause, Maximize } from 'lucide-react';
import { AnimatedTitle } from '../animated-title';
import { VideoData } from '@/lib/utils/media.utils';

interface VideoPlayerProps {
  thumbnailSrc: string;
  videoData: VideoData | null;
}

export function VideoPlayer({ thumbnailSrc, videoData }: VideoPlayerProps) {
  const videoSrc = videoData?.encoded_urls?.['1080']?.url 
  ||videoData?.encoded_urls?.['720']?.url 
  || videoData?.encoded_urls?.['480']?.url 
  || videoData?.original_url || null;
  const lowVideoSrc = videoData?.encoded_urls?.['480']?.url || videoData?.original_url || null;

  // State management
  const [isShowreelExpanded, setIsShowreelExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(undefined);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const hideControlsTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Format time (seconds to MM:SS)
  const formatTime = (timeInSeconds: number): string => {
    if (isNaN(timeInSeconds)) return '00:00';
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Mobil cihaz kontrolü
  useEffect(() => {
    // Ekran genişliği 1024px'den küçükse mobil olarak işaretle
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    // İlk yükleme için kontrol
    checkIfMobile();

    // Ekran boyutu değiştiğinde dinle
    window.addEventListener('resize', checkIfMobile);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Handle keyboard events and controls visibility
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isShowreelExpanded) return;

      switch (e.key) {
        case 'Escape':
          toggleShowreel();
          break;
        case ' ':
        case 'k':
          togglePlayPause();
          e.preventDefault();
          break;
        case 'm':
          toggleMute();
          break;
        case 'f':
          toggleFullscreen();
          break;
        case 'ArrowRight':
          if (videoRef.current) videoRef.current.currentTime += 5;
          break;
        case 'ArrowLeft':
          if (videoRef.current) videoRef.current.currentTime -= 5;
          break;
      }
    };

    // Show controls when mouse moves, hide after inactivity
    const handleMouseMove = () => {
      if (!isShowreelExpanded) return;
      showControls();
    };

    // Prevent scrolling when expanded
    if (isShowreelExpanded) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('mousemove', handleMouseMove);
    } else {
      document.body.style.overflow = '';
      if (hideControlsTimeout.current !== null) {
        clearTimeout(hideControlsTimeout.current);
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousemove', handleMouseMove);
      document.body.style.overflow = '';
      if (hideControlsTimeout.current !== null) {
        clearTimeout(hideControlsTimeout.current);
      }
    };
  }, [isShowreelExpanded, isPlaying, isMuted]);

  // Show/hide controls with timeout
  const showControls = useCallback(() => {
    setControlsVisible(true);
    if (hideControlsTimeout.current !== null) {
      clearTimeout(hideControlsTimeout.current);
    }
    hideControlsTimeout.current = setTimeout(() => {
      if (isPlaying) setControlsVisible(false);
    }, 3000);
  }, [isPlaying]);

  // Update video progress
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateProgress = () => {
      setProgress(video.currentTime);
    };

    const handleVideoPlay = () => {
      console.log('Video playing');
      setIsPlaying(true);
      showControls();
    };

    const handleVideoPause = () => {
      console.log('Video paused');
      setIsPlaying(false);
      setControlsVisible(true);
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setControlsVisible(true);
    };

    video.addEventListener('timeupdate', updateProgress);
    video.addEventListener('play', handleVideoPlay);
    video.addEventListener('pause', handleVideoPause);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', updateProgress);
      video.removeEventListener('play', handleVideoPlay);
      video.removeEventListener('pause', handleVideoPause);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('ended', handleEnded);
    };
  }, [isShowreelExpanded, showControls]);

  // Toggle video play/pause
  const togglePlayPause = useCallback(() => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(err => console.error('Error playing video:', err));
    }
    showControls();
  }, [isPlaying, showControls]);

  // Toggle mute state
  const toggleMute = useCallback(() => {
    if (!videoRef.current) return;

    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(!isMuted);
    showControls();
  }, [isMuted, showControls]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!videoContainerRef.current) return;

    if (document.fullscreenElement) {
      document.exitFullscreen().catch(err => console.error('Error exiting fullscreen:', err));
    } else {
      videoContainerRef.current.requestFullscreen().catch(err => console.error('Error entering fullscreen:', err));
    }
    showControls();
  }, [showControls]);

  // Handle seeking
  const handleSeek = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!videoRef.current) return;

      const newTime = parseFloat(e.target.value);
      videoRef.current.currentTime = newTime;
      setProgress(newTime);
      showControls();
    },
    [showControls]
  );

  // Toggle showreel expanded state
  const toggleShowreel = () => {
    console.log('Toggle showreel called!');
    setIsShowreelExpanded(prev => {
      const willExpand = !prev;
      console.log('Showreel will expand:', willExpand);

      // Force layout recalculation to help animation
      document.body.offsetHeight;

      // Play/pause video based on expanded state
      if (willExpand) {
        // Don't reset scroll position since we're staying within MegaHero
        setIsLoading(true);
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.play().catch(err => {
              console.error('Error playing video:', err);
              setIsLoading(false);
            });
          }
        }, 300); // Short delay to allow transition to complete
      } else {
        if (videoRef.current) {
          videoRef.current.pause();
          setIsPlaying(false);
        }
      }

      return willExpand;
    });
  };

  // Handle video load completion
  const handleVideoLoad = () => {
    setIsLoading(false);
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  return (
    <div
      className={`shadow-md transition-all 
        duration-500 
        z-500
        ease-in-out main-video-player ${isShowreelExpanded ? 'main-video-player-expanded rounded-none' : 'rounded-sm'}`}
      style={{
        backgroundColor: 'black',
        cursor: isShowreelExpanded ? 'default' : 'pointer',
        transformOrigin: 'bottom right',
      }}
    >
      <div className="absolute top-[-25px] lg:left-0 z-50">
        <AnimatedTitle>[ SHOWREEL ]</AnimatedTitle>
      </div>

      {/* Thumbnail image when collapsed */}
      {!isShowreelExpanded &&
        (thumbnailSrc ? (
          <div
            className="w-full h-full flex items-center justify-center relative cursor-pointer"
            style={{
              backgroundImage: `url(${thumbnailSrc})`,
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
            onClick={e => {
              console.log('Thumbnail clicked!');
              e.stopPropagation();
              toggleShowreel();
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* Enlarge icon in top-left corner - ALWAYS VISIBLE */}
            {isHovered && (
              <div
                className="absolute inset-0 z-30 flex items-center justify-center
         bg-black/50 rounded-sm hover:bg-opacity-80 transition-all duration-300 outline-hidden border-none cursor-pointer"
              >
                <button
                  className="z-30 outline-hidden border-none cursor-pointer"
                  onClick={e => {
                    console.log('Maximize button clicked');
                    e.stopPropagation(); // Prevent double triggering with parent click
                    toggleShowreel();
                  }}
                  aria-label="Enlarge showreel"
                >
                  <Maximize2 size={18} className="text-white rotate-90" />
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="w-full h-full rounded-md overflow-hidden">
            {isMobile === undefined ? (
              <></>
            ) : isMobile ? (
              <video
                ref={videoRef}
                src={lowVideoSrc}
                className="w-full h-full object-contain"
                autoPlay={false}
                muted
                loop
                onClick={toggleShowreel}
              />
            ) : (
              <video
                ref={videoRef}
                src={lowVideoSrc}
                className="w-full h-full object-contain"
                autoPlay={true}
                muted
                loop
                onClick={toggleShowreel}
              />
            )}
          </div>
        ))}

      {/* Video when expanded */}
      {isShowreelExpanded && (
        <div
          ref={videoContainerRef}
          className="relative w-full h-full"
          onMouseMove={showControls}
          onClick={togglePlayPause}
        >
          {/* Loading spinner */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-t-white border-white/30"></div>
            </div>
          )}

          {/* Video element */}
          <video
            ref={videoRef}
            className="w-full h-full object-contain"
            src={videoSrc}
            autoPlay={true}
            playsInline
            onLoadedData={handleVideoLoad}
            onCanPlay={handleVideoLoad}
            onClick={e => e.stopPropagation()}
          />

          {/* Custom video controls */}
          <div
            className="absolute bottom-0 left-0 right-0 px-2 pb-5 pt-4 
            bg-linear-to-t from-black/70 to-transparent transition-opacity duration-300"
            style={{ opacity: controlsVisible ? 1 : 0 }}
            onClick={e => e.stopPropagation()}
          >
            {/* All controls in a single row */}
            <div className="flex items-center gap-2 w-full">
              {/* Play/Pause button */}
              <button
                className="text-white hover:text-white/80 transition-colors"
                onClick={togglePlayPause}
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? <Pause size={20} /> : <Play size={20} />}
              </button>

              {/* Current time */}
              <span className="text-white text-xs">{formatTime(progress)}</span>

              {/* Progress bar */}
              <input
                type="range"
                min="0"
                max={duration || 100}
                value={progress}
                onChange={handleSeek}
                className="grow h-1 bg-white/30 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, white ${(progress / (duration || 100)) * 100}%, rgba(255,255,255,0.3) ${(progress / (duration || 100)) * 100}%)`,
                }}
              />

              {/* Duration */}
              <span className="text-white text-xs">{formatTime(duration)}</span>

              {/* Mute/Unmute button */}
              <button
                className="text-white hover:text-white/80 transition-colors ml-1"
                onClick={toggleMute}
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>

              {/* Fullscreen button */}
              <button
                className="text-white hover:text-white/80 transition-colors ml-1"
                onClick={toggleFullscreen}
                aria-label="Fullscreen"
              >
                <Maximize size={18} />
              </button>

              {/* Close button */}
              <button
                className="text-white hover:text-white/80 transition-colors ml-1"
                onClick={e => {
                  e.stopPropagation();
                  toggleShowreel();
                }}
                aria-label="Close video"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Large play/pause button in center */}
          {!isLoading && !isPlaying && (
            <button
              className="absolute inset-0 m-auto w-20 h-20 bg-white/20 rounded-full flex items-center justify-center"
              onClick={togglePlayPause}
              style={{ opacity: controlsVisible ? 1 : 0 }}
            >
              <Play size={40} className="text-white" style={{ marginLeft: '4px' }} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
